import { useCallback, useMemo, useState, type FC, type ReactNode } from "react";
import { AppSearchContext } from "../../app/appSearchContext.js";
import { useKeyboardShortcutEffect } from "../keyboardShortcuts/keyboardShortcutEffect.js";
import {
  useSearchDialogContext,
  type SearchContextType,
} from "./searchContexts.js";
import { searchKeyboardShortcut } from "./searchKeyboardShortcut.js";
import { SearchModalOverlay } from "./searchModalOverlay.js";

export interface AppSearchProviderProps {
  children: ReactNode;
}

/**
 * Owns the single search modal for the app. The navigation renders its trigger
 * more than once (docked + floating), so open state and the modal live here to
 * avoid opening a second modal, and the active section registers its context.
 */
export const AppSearchProvider: FC<AppSearchProviderProps> = ({ children }) => {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchContext, setSearchContext] = useState<SearchContextType>();

  const openSearch = useCallback(() => {
    setIsSearchActive(true);
  }, []);

  const registerSearchContext = useCallback((context: SearchContextType) => {
    setSearchContext(context);
    return () => {
      setSearchContext((current) =>
        current === context ? undefined : current,
      );
    };
  }, []);

  useKeyboardShortcutEffect(searchKeyboardShortcut, () => {
    // Ignore the shortcut on sections without search (no registered context).
    if (searchContext !== undefined) {
      setIsSearchActive(true);
    }
  });

  const value = useMemo(
    () => ({ isSearchActive, openSearch, registerSearchContext }),
    [isSearchActive, openSearch, registerSearchContext],
  );

  return (
    <AppSearchContext.Provider value={value}>
      {children}
      {searchContext !== undefined ? (
        <ActiveSearchModal
          context={searchContext}
          isOpen={isSearchActive}
          onOpenChange={setIsSearchActive}
        />
      ) : null}
    </AppSearchContext.Provider>
  );
};

interface ActiveSearchModalProps {
  context: SearchContextType;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

// Split out so `useSearchDialogContext` only runs with a defined context.
const ActiveSearchModal: FC<ActiveSearchModalProps> = ({
  context,
  isOpen,
  onOpenChange,
}) => {
  const dialogContext = useSearchDialogContext(context);

  return (
    <SearchModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      {...dialogContext}
    />
  );
};
